/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Page News Preview component
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { FC, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import { Button } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { PageTitle } from '@/main/components/common';
import Title from '@/main/components/common/Title/Title';
import { appHistoryPush } from '@/main/modules/app/appHistory';
import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import { makeSelectPageNewsTranslation } from '@/main/modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@/main/modules/internationalization/types/common/pages';
import { modalCloseAll } from '@/main/modules/modal/modalActions';
import { EModalStatus } from '@/main/modules/modal/types/modalTypes';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import { useInternalNewsSubmit } from './components/NewsForms/hooks/useInternalNewsSubmit';
import { useOpenPublishModal } from './components/NewsForms/hooks/useOpenPublishModal';
import NewsList from './components/NewsList';
import NewsView from './components/NewsView';
import { ECreateNewsTypes, ENewsFormCreationFields, ERecipientTypes } from './constants';
import { PageNewsPreviewContolsStyled } from './styled/PageNewsPreviewStyled';
import { PageNewsContainerStyled, PageNewsWrapperStyled } from './styled/PageNewsStyled';
import { INewsItem, INewsListItem, IPreviewLocationState } from './types';
import { base64toFile } from './utils/fileUtils';

const PageNewsPreview: FC = () => {
    const dispatch = useDispatch();

    const history = useHistory();

    const { previewNews: translation } = useSelector(makeSelectPageNewsTranslation());

    const { state: locationState } = useLocation<IPreviewLocationState>();

    const hanldeBackClick = useCallback(() => {
        appHistoryPush(RoutePath.newsCreate, locationState);
    }, [locationState]);

    useLayoutEffect(() => {
        if (!locationState) {
            appHistoryPush(RoutePath.newsCreate);
        }
    });

    useEffect(() => {
        if (history.action === 'POP') {
            hanldeBackClick();
        }
    }, [hanldeBackClick, history.action]);

    const [publish, { isError, error }] = useInternalNewsSubmit();

    const openPublishModal = useOpenPublishModal();

    const previewNewsList: INewsListItem[] = useMemo(() => {
        if (!locationState) return [];
        return [
            {
                id: 1,
                title: locationState.title,
            },
        ];
    }, [locationState]);

    const previewNewsItem: INewsItem | undefined = useMemo(() => {
        if (!locationState) return undefined;
        return {
            id: 1,
            content: locationState.markdown,
            imageLinks: locationState.images.map((el, index) => ({ id: index, link: el })),
        };
    }, [locationState]);

    const handleCreate = useCallback(() => {
        if (locationState) {
            const { fileBase64, recipientType, ...data } = locationState;
            // Декодируем файл для отправки на сервер
            if (fileBase64) {
                const file = base64toFile(fileBase64, 'recipients.xlsx');
                const publishData = {
                    ...data,
                    [ENewsFormCreationFields.USER_FILE]: file,
                };
                publish(publishData, recipientType);
            } else {
                publish(data, ERecipientTypes.ALL);
            }
        }
    }, [locationState, publish]);

    const handleSubmit = useCallback(() => {
        if (locationState) {
            openPublishModal(ECreateNewsTypes.INTERNAL, EModalStatus.start, { params: locationState }, handleCreate);
        }
    }, [handleCreate, locationState, openPublishModal]);

    useEffect(() => {
        if (isError) {
            dispatch(modalCloseAll());
            openPublishModal(ECreateNewsTypes.INTERNAL, EModalStatus.failure, { apiError: error });
        }
    }, [dispatch, error, handleCreate, isError, locationState, openPublishModal]);

    return (
        <PageNewsContainerStyled>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageNews]} />
            <Title pageName={ECommonTranslationPages.news} />
            <PageTitle title={translation.header.title} />
            <PageNewsWrapperStyled>
                <NewsList news={previewNewsList} />
                <NewsView item={previewNewsItem} />
            </PageNewsWrapperStyled>
            <PageNewsPreviewContolsStyled>
                <Button color="info" size="small" variant="text" onClick={hanldeBackClick}>
                    {translation.controls.back}
                </Button>
                <Button  size="small" typographyProps={{ variant: 'h5' }} onClick={handleSubmit}>
                    {translation.controls.submit}
                </Button>
            </PageNewsPreviewContolsStyled>
        </PageNewsContainerStyled>
    );
};

export default PageNewsPreview;
